import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Ruler, Paperclip, Calendar, FileCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { instrumentService, attachmentService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CalibrationStatusBadge from '../components/CalibrationStatusBadge';

const emptyCal = {
  calibration_date: new Date().toISOString().split('T')[0],
  expiry_date: '',
  certifying_body: '',
  certificate_number: '',
  result: 'OK',
  notes: '',
};

const RESULT_BADGE = {
  OK: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
  NOK: 'bg-red-100 text-red-700 border border-red-300',
  conditional: 'bg-amber-100 text-amber-800 border border-amber-300',
};

const RESULT_LABEL = { OK: 'Conforme', NOK: 'Non conforme', conditional: 'Condizionale' };

export default function InstrumentDetailPage() {
  const { id } = useParams();
  const { hasRole } = useAuth();
  const [inst, setInst] = useState(null);
  const [calibrations, setCalibrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calDialog, setCalDialog] = useState(false);
  const [calForm, setCalForm] = useState(emptyCal);
  const [pendingFile, setPendingFile] = useState(null);
  const [deleteCalId, setDeleteCalId] = useState(null);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    try {
      const [i, c] = await Promise.all([
        instrumentService.getById(id),
        instrumentService.listCalibrations(id),
      ]);
      setInst(i.data);
      setCalibrations(c.data || []);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  const openCalDialog = () => { setCalForm(emptyCal); setPendingFile(null); setCalDialog(true); };

  const handleSubmitCal = async () => {
    if (!calForm.calibration_date) { toast.error('Data taratura obbligatoria'); return; }
    try {
      const payload = {
        calibration_date: calForm.calibration_date,
        expiry_date: calForm.expiry_date || null,
        certifying_body: calForm.certifying_body || null,
        certificate_number: calForm.certificate_number || null,
        result: calForm.result,
        notes: calForm.notes || null,
      };
      const r = await instrumentService.registerCalibration(id, payload);
      const cal = r.data;
      if (pendingFile && cal?.id) {
        const ur = await attachmentService.upload(pendingFile, 'instrument_calibration', cal.id, 'calibration_certificate', calForm.certificate_number || 'Certificato');
        const att = ur.data;
        // Update calibration with attachment + propagate to instrument
        await instrumentService.update(id, { certificate_url: att.file_url, certificate_name: att.file_name });
      }
      toast.success('Taratura registrata');
      setCalDialog(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore registrazione');
    }
  };

  const handleDeleteCal = async () => {
    try {
      await instrumentService.deleteCalibration(deleteCalId);
      toast.success('Taratura eliminata');
      setDeleteCalId(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const openAttachment = (url) => {
    if (!url) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API_URL}${url}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!inst) return <div className="py-12 text-center text-slate-500">Strumento non trovato</div>;

  return (
    <div className="space-y-6" data-testid="instrument-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/instruments"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Indietro</Button></Link>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <Ruler className="w-6 h-6 text-slate-700" />
              <h1 className="text-2xl font-bold text-slate-900 font-mono">{inst.code}</h1>
              <CalibrationStatusBadge status={inst.calibration_status} days={inst.days_to_expiry} />
              <Badge className={`${inst.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'} border`}>{inst.status === 'active' ? 'Attivo' : inst.status}</Badge>
            </div>
            <p className="text-slate-600 text-base mt-1">{inst.name}</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Caratteristiche</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><p className="text-xs uppercase font-bold text-slate-500">Tipo</p><p className="mt-1 capitalize">{inst.instrument_type?.replace(/_/g, ' ')}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Costruttore</p><p className="mt-1">{inst.manufacturer || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Modello</p><p className="mt-1 font-mono">{inst.model || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">SN</p><p className="mt-1 font-mono">{inst.serial_number || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Range Misura</p><p className="mt-1 font-mono">{inst.measurement_range || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Risoluzione</p><p className="mt-1 font-mono">{inst.resolution || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Accuratezza</p><p className="mt-1 font-mono">{inst.accuracy || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Ubicazione</p><p className="mt-1">{inst.location || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Responsabile</p><p className="mt-1">{inst.owner || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Intervallo</p><p className="mt-1">{inst.calibration_interval_months} mesi</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Ultima Taratura</p><p className="mt-1 font-medium">{inst.last_calibration_date || '-'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-500">Prossima Taratura</p><p className="mt-1 font-medium">{inst.next_calibration_date || '-'}</p></div>
          </div>
          {inst.notes && <p className="mt-3 text-sm text-slate-600 italic">{inst.notes}</p>}
        </CardContent>
      </Card>

      {/* Calibrations history */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Calendar className="w-5 h-5 text-slate-700" />Storico Tarature ({calibrations.length})</CardTitle>
          {hasRole('admin', 'quality') && (
            <Button onClick={openCalDialog} className="h-10 px-4" data-testid="add-calibration-btn"><Plus className="w-4 h-4 mr-2" />Registra Taratura</Button>
          )}
        </CardHeader>
        <CardContent>
          {calibrations.length === 0 ? (
            <p className="text-center text-slate-500 py-8 italic">Nessuna taratura registrata</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Data</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Scadenza</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Ente</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">N. Certificato</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Esito</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Allegato</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Note</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {calibrations.map(c => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{c.calibration_date}</td>
                      <td className="px-3 py-2">{c.expiry_date || '-'}</td>
                      <td className="px-3 py-2">{c.certifying_body || '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{c.certificate_number || '-'}</td>
                      <td className="px-3 py-2"><Badge className={RESULT_BADGE[c.result] || RESULT_BADGE.OK}>{RESULT_LABEL[c.result] || c.result}</Badge></td>
                      <td className="px-3 py-2">
                        {c.certificate_url ? (
                          <button onClick={() => openAttachment(c.certificate_url)} className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-800">
                            <Paperclip className="w-3.5 h-3.5" /><span className="truncate max-w-[120px]">{c.certificate_name || 'cert'}</span>
                          </button>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2 text-slate-500 truncate max-w-[200px]">{c.notes || '-'}</td>
                      <td className="px-3 py-2 text-right">
                        {hasRole('admin', 'quality') && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => setDeleteCalId(c.id)}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Register calibration dialog */}
      <Dialog open={calDialog} onOpenChange={setCalDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileCheck className="w-5 h-5" />Registra Taratura</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-4">
            <div className="space-y-1"><Label>Data Taratura *</Label><Input type="date" value={calForm.calibration_date} onChange={e => setCalForm(f => ({ ...f, calibration_date: e.target.value }))} data-testid="cal-date" /></div>
            <div className="space-y-1"><Label>Data Scadenza (auto se vuoto)</Label><Input type="date" value={calForm.expiry_date} onChange={e => setCalForm(f => ({ ...f, expiry_date: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Ente Certificatore</Label><Input value={calForm.certifying_body} onChange={e => setCalForm(f => ({ ...f, certifying_body: e.target.value }))} placeholder="es. LAT 094" /></div>
            <div className="space-y-1"><Label>N. Certificato</Label><Input value={calForm.certificate_number} onChange={e => setCalForm(f => ({ ...f, certificate_number: e.target.value }))} placeholder="es. LAT-2026/00123" /></div>
            <div className="space-y-1"><Label>Esito</Label>
              <Select value={calForm.result} onValueChange={v => setCalForm(f => ({ ...f, result: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OK">Conforme</SelectItem>
                  <SelectItem value="NOK">Non conforme</SelectItem>
                  <SelectItem value="conditional">Condizionale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2"><Label>Note</Label><Textarea rows={2} value={calForm.notes} onChange={e => setCalForm(f => ({ ...f, notes: e.target.value }))} /></div>
            <div className="space-y-1 md:col-span-2">
              <Label>Allegato Certificato (PDF/immagine)</Label>
              <Input type="file" accept="application/pdf,image/*" onChange={e => setPendingFile(e.target.files?.[0] || null)} data-testid="cal-file" />
              {pendingFile && <p className="text-xs text-slate-500 mt-1">File: {pendingFile.name}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalDialog(false)}>Annulla</Button>
            <Button onClick={handleSubmitCal} data-testid="cal-submit-btn">Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteCalId} onOpenChange={(o) => { if (!o) setDeleteCalId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Conferma eliminazione</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 py-2">Eliminare questa taratura? La data di prossima taratura dello strumento verra ricalcolata.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCalId(null)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDeleteCal}>Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
